import React from 'react'

import { FormattedMessage, useIntl } from 'react-intl'

import TradePriceIcon from 'app/assets/icons/profile/trade-price-icon.svg'
import Stack from 'app/components/base/Stack'
import Text from 'app/components/base/Text'
import Tooltip from 'app/components/base/Tooltip'
import useAppStore from 'app/store/app.store'
import { IS_NATIVE_APP } from 'app/utils/constants/env.constants'
import { defaultCurrency } from 'app/utils/constants/intl.constants'

interface TradeBusinessPriceProps {
  actualPrice: number
  isTradePrice?: boolean
  listingCurrency?: Currency
  tradePrice?: number
}

const TradeBusinessPrice = ({
  actualPrice,
  isTradePrice,
  listingCurrency,
  tradePrice = 0
}: TradeBusinessPriceProps) => {
  const intl = useIntl()
  const isTablet = useAppStore((state) => state.isTablet)

  const tradePriceWithCurrency = intl.formatNumber(tradePrice, {
    currency: listingCurrency?.name || defaultCurrency,
    style: 'currency'
  })

  const actualPriceWithCurrency = intl.formatNumber(actualPrice, {
    currency: listingCurrency?.name || defaultCurrency,
    style: 'currency'
  })

  const appDirection = isTablet ? 'row' : 'column'

  return (
    <Stack
      direction={IS_NATIVE_APP ? appDirection : { xs: 'column', sm: 'row' }}
      spacing={{
        ...(!IS_NATIVE_APP && { xs: 0, sm: 4 }),
        ...(IS_NATIVE_APP && { xs: 0, md: 4 })
      }}
      sx={{
        alignItems: IS_NATIVE_APP
          ? { xs: 'flex-start', md: 'center' }
          : { xs: 'flex-start', sm: 'center' }
      }}
    >
      <Stack direction="column">
        <Text variant="h4" fontWeight={700}>
          <FormattedMessage defaultMessage="Trade Price" />
        </Text>

        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <Text variant="h2" color="secondary.third">
            {tradePriceWithCurrency}
          </Text>

          {isTradePrice && (
            <Tooltip
              isCloseButton
              content={
                <Text
                  variant="subtitle1"
                  sx={{
                    color: 'common.white',
                    ...(!IS_NATIVE_APP && { mr: 0.5 }),
                    ...(IS_NATIVE_APP && { maxWidth: 242 })
                  }}
                >
                  <FormattedMessage defaultMessage="This seller offers trade prices instead of retail prices for business customers." />
                </Text>
              }
              sx={{ display: 'flex', alignItems: 'center' }}
            >
              <TradePriceIcon width="30px" height="30px" viewBox="0 0 24 24" />
            </Tooltip>
          )}
        </Stack>
      </Stack>

      <Stack direction="column">
        <Text variant="h4" fontWeight={700} color="text.secondary">
          <FormattedMessage defaultMessage="Retail Price" />
        </Text>

        <Text variant="h2" color="text.secondary">
          {actualPriceWithCurrency}
        </Text>
      </Stack>
    </Stack>
  )
}

export default TradeBusinessPrice
